import type { Asset, Tenant } from "../../types/deal";
import { excelCellToDate, toDateOnlyString } from "../../utils/formatDate";

type CountryOption = { id: number; nicename: string };
type SectorOption = { id: number; sector: string };

export type ParseDealExcelResult = {
  assets: Partial<Asset>[];
  tenants: Tenant[];
  sheetsFound: string[];
};

function makeTempId(prefix: string, index: number) {
  return `${prefix}-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Convert Excel serial dates / Date / string into a local-midnight JS Date,
 * or null. Text is read as UK dd/mm/yyyy or ISO YYYY-MM-DD.
 */
function convertExcelDate(value: unknown): Date | null {
  return excelCellToDate(value);
}

function processAssetRow(
  asset: any,
  countries: CountryOption[],
  sectors: SectorOption[],
): Partial<Asset> {
  const processedAsset = { ...asset };

  if (processedAsset.valuation_date) {
    processedAsset.valuation_date = convertExcelDate(
      processedAsset.valuation_date,
    );
  }
  if (processedAsset.acquisition_date) {
    processedAsset.acquisition_date = convertExcelDate(
      processedAsset.acquisition_date,
    );
  }
  if (processedAsset.created_at) {
    processedAsset.created_at = convertExcelDate(processedAsset.created_at);
  }
  if (processedAsset.updated_at) {
    processedAsset.updated_at = convertExcelDate(processedAsset.updated_at);
  }

  if (processedAsset.country && typeof processedAsset.country === "string") {
    const matchingCountry = countries.find(
      (country) =>
        country.nicename.toLowerCase() ===
        processedAsset.country.toLowerCase(),
    );
    if (matchingCountry) {
      processedAsset.country = matchingCountry;
    }
  }

  if (processedAsset.deal_id && typeof processedAsset.deal_id === "number") {
    processedAsset.deal = { id: processedAsset.deal_id };
    delete processedAsset.deal_id;
  }

  if (processedAsset.sector && typeof processedAsset.sector === "string") {
    const matchingSector = sectors.find(
      (sector) =>
        sector.sector.toLowerCase() === processedAsset.sector.toLowerCase(),
    );
    if (matchingSector) {
      processedAsset.sector = matchingSector;
    }
  }

  return processedAsset;
}

function processTenantRow(tenant: any): Tenant {
  const processedTenant = { ...tenant } as Tenant;

  const dateFields = [
    "lease_start_date",
    "lease_end_date",
    "break_option_date",
    "next_review_date",
    "rent_free_end_date",
  ] as const;

  dateFields.forEach((field) => {
    if (processedTenant[field]) {
      const date = convertExcelDate(processedTenant[field]);
      (processedTenant as any)[field] = date ? toDateOnlyString(date) : null;
    }
  });

  return processedTenant;
}

/**
 * Parse a deal Excel template (Assets + Tenants sheets).
 * Returns rows ready to drop into DealContext tables.
 */
export async function parseDealExcelUpload(
  file: File,
  countries: CountryOption[],
  sectors: SectorOption[],
): Promise<ParseDealExcelResult> {
  const arrayBuffer = await file.arrayBuffer();
  const XLSX = await import("xlsx");
  const workbook = XLSX.read(arrayBuffer, { type: "array" });

  const sheetsFound: string[] = [];
  let assets: Partial<Asset>[] = [];
  let tenants: Tenant[] = [];

  const assetsSheet = workbook.Sheets["Assets"];
  if (assetsSheet) {
    const rawAssets = XLSX.utils.sheet_to_json(assetsSheet) as any[];
    assets = rawAssets.map((row) =>
      processAssetRow(row, countries, sectors),
    );

    assets.forEach((asset: any, index: number) => {
      if (!asset.id || typeof asset.id !== "number") {
        asset.id = makeTempId("tmp-asset", index);
      }
      delete asset.shouldCreate;
    });

    sheetsFound.push("Assets");
  }

  const tenantsSheet = workbook.Sheets["Tenants"];
  if (tenantsSheet) {
    const rawTenants = XLSX.utils.sheet_to_json(tenantsSheet) as any[];
    tenants = rawTenants.map((row, index) => {
      const tenant: any = processTenantRow(row);
      if (!tenant.id || typeof tenant.id !== "number") {
        tenant.id = makeTempId("tmp-tenant", index);
      }
      tenant.shouldCreate = true;
      tenant.included = true;
      return tenant;
    });

    // Match tenants to assets by asset_id or asset_name
    if (assets.length > 0 && tenants.length > 0) {
      assets.forEach((asset) => {
        asset.tenants = [];
      });

      tenants = tenants.map((tenant: any) => {
        const matchingAsset = assets.find((asset: any) => {
          if (asset.id && tenant.asset_id) {
            return (
              asset.id.toString().toLowerCase().trim() ===
              tenant.asset_id.toString().toLowerCase().trim()
            );
          }
          if (asset.asset_name && tenant.asset_name) {
            return (
              asset.asset_name.toString().toLowerCase().trim() ===
              tenant.asset_name.toString().toLowerCase().trim()
            );
          }
          return false;
        });

        if (matchingAsset) {
          const linked = {
            ...tenant,
            asset_name: matchingAsset.asset_name,
            asset: matchingAsset,
            asset_id: matchingAsset.id,
          };
          if (!matchingAsset.tenants) matchingAsset.tenants = [];
          matchingAsset.tenants.push(linked as any);
          return linked;
        }

        return tenant;
      });
    }

    sheetsFound.push("Tenants");
  }

  return { assets, tenants, sheetsFound };
}
